import type { InteractiveReportKey, ReportComparisonMode } from '@query'
import dayjs from 'dayjs'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createDefaultColumn, syncColumnIdSequence, type PeriodColumn } from './periodUtils'

export type ReportExpansion = {
  headers: Record<string, boolean>
  items: Record<string, boolean>
  /** Accounting trees default-open section headers once, then keep the user's toggles. */
  headersReady: boolean
}

export type InteractiveReportsSession = {
  v: 1
  reportKey: InteractiveReportKey
  tagIds: number[]
  columns: PeriodColumn[]
  showComparison: boolean
  comparisonMode: ReportComparisonMode
  expansion: Partial<Record<InteractiveReportKey, ReportExpansion>>
}

const REPORT_KEYS: readonly InteractiveReportKey[] = [
  'balance_sheet',
  'income_statement',
  'ledger',
  'journal',
  'balance_sheet_specifications',
  'vat',
  'eu_vat'
]

export const emptyReportExpansion = (): ReportExpansion => ({
  headers: {},
  items: {},
  headersReady: false
})

export const getReportExpansion = (
  session: InteractiveReportsSession,
  reportKey: InteractiveReportKey
): ReportExpansion => session.expansion[reportKey] ?? emptyReportExpansion()

const storageKey = (slug: string) => `nocfo.interactiveReports.v1.${slug}`

const defaultSession = (): InteractiveReportsSession => ({
  v: 1,
  reportKey: 'income_statement',
  tagIds: [],
  columns: [
    createDefaultColumn(
      {
        key: 'period-this',
        date_from: dayjs().startOf('month').format('YYYY-MM-DD'),
        date_to: dayjs().endOf('month').format('YYYY-MM-DD')
      },
      'income_statement'
    )
  ],
  showComparison: true,
  comparisonMode: 'amount',
  expansion: {}
})

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isStringRecord = (value: unknown): value is Record<string, boolean> =>
  isRecord(value) && Object.values(value).every(entry => typeof entry === 'boolean')

const optionalString = (value: unknown) => (typeof value === 'string' ? value : undefined)

const parseComparisonMode = (value: unknown): ReportComparisonMode =>
  value === 'percent' ? 'percent' : 'amount'

const parseExpansion = (value: unknown): ReportExpansion | null => {
  if (!isRecord(value)) return null
  if (!isStringRecord(value.headers) || !isStringRecord(value.items)) return null
  if (typeof value.headersReady !== 'boolean') return null
  return {
    headers: value.headers,
    items: value.items,
    headersReady: value.headersReady
  }
}

const parseColumns = (value: unknown): PeriodColumn[] | null => {
  if (!Array.isArray(value) || value.length === 0) return null
  const columns: PeriodColumn[] = []
  for (const entry of value) {
    if (!isRecord(entry) || typeof entry.id !== 'string' || typeof entry.preset !== 'string') {
      return null
    }
    columns.push({
      id: entry.id,
      preset: entry.preset as PeriodColumn['preset'],
      asOf: optionalString(entry.asOf),
      dateFrom: optionalString(entry.dateFrom),
      dateTo: optionalString(entry.dateTo),
      title: optionalString(entry.title)
    })
  }
  return columns
}

const parseSession = (raw: string): InteractiveReportsSession | null => {
  try {
    const value: unknown = JSON.parse(raw)
    if (!isRecord(value) || value.v !== 1) return null
    if (
      typeof value.reportKey !== 'string' ||
      !REPORT_KEYS.includes(value.reportKey as InteractiveReportKey)
    ) {
      return null
    }
    if (!Array.isArray(value.tagIds) || value.tagIds.some(id => typeof id !== 'number')) {
      return null
    }
    const columns = parseColumns(value.columns)
    if (!columns) return null
    if (typeof value.showComparison !== 'boolean') return null
    if (!isRecord(value.expansion)) return null

    const expansion: InteractiveReportsSession['expansion'] = {}
    for (const key of REPORT_KEYS) {
      if (!(key in value.expansion)) continue
      const parsed = parseExpansion(value.expansion[key])
      if (parsed) expansion[key] = parsed
    }

    return {
      v: 1,
      reportKey: value.reportKey as InteractiveReportKey,
      tagIds: value.tagIds,
      columns,
      showComparison: value.showComparison,
      comparisonMode: parseComparisonMode(value.comparisonMode),
      expansion
    }
  } catch {
    return null
  }
}

const readSession = (slug: string): InteractiveReportsSession | null => {
  try {
    const raw = window.sessionStorage.getItem(storageKey(slug))
    if (raw == null) return null
    const parsed = parseSession(raw)
    if (parsed) syncColumnIdSequence(parsed.columns)
    return parsed
  } catch {
    return null
  }
}

const writeSession = (slug: string, session: InteractiveReportsSession) => {
  try {
    window.sessionStorage.setItem(storageKey(slug), JSON.stringify(session))
  } catch {
    // Private mode / quota — keep in-memory state.
  }
}

export const rememberInteractiveReportKey = (slug: string, reportKey: InteractiveReportKey) => {
  const current = readSession(slug) ?? defaultSession()
  writeSession(slug, { ...current, reportKey })
}

export const openInteractiveEuSalesReport = (
  history: { push: (path: string) => unknown },
  slug: string
) => {
  rememberInteractiveReportKey(slug, 'eu_vat')
  history.push(`/${slug}/reporting`)
}

export const useInteractiveReportsSession = (slug: string | undefined) => {
  const restoredRef = useRef(false)
  const previousSlugRef = useRef(slug)
  const [session, setSessionState] = useState<InteractiveReportsSession>(() => {
    if (!slug) return defaultSession()
    const saved = readSession(slug)
    restoredRef.current = Boolean(saved)
    return saved ?? defaultSession()
  })

  useEffect(() => {
    if (!slug || previousSlugRef.current === slug) return
    previousSlugRef.current = slug
    const saved = readSession(slug)
    restoredRef.current = Boolean(saved)
    setSessionState(saved ?? defaultSession())
  }, [slug])

  const setSession = useCallback(
    (
      value:
        | InteractiveReportsSession
        | ((prev: InteractiveReportsSession) => InteractiveReportsSession)
    ) => {
      setSessionState(prev => {
        const next = typeof value === 'function' ? value(prev) : value
        if (slug) writeSession(slug, next)
        return next
      })
    },
    [slug]
  )

  const consumeRestoredSession = useCallback(() => {
    const restored = restoredRef.current
    restoredRef.current = false
    return restored
  }, [])

  return { session, setSession, consumeRestoredSession }
}
